import { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '../../../services/api';
import DataTable from '../componentes/DataTable';
import DeleteConfirm from '../componentes/DeleteConfirm';
import Badge from '../componentes/Badge';
import SearchBar from '../componentes/SearchBar';
import { getImageUrl } from '../utils/imageUrl';
import { Plus, Camera, X, Loader2 } from 'lucide-react';

// ── Helpers de fecha ─────────────────────────────────────
const formatFecha = (dateStr) => {
  if (!dateStr || dateStr.startsWith('0000')) return '—';
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const calcularEdad = (dateStr) => {
  if (!dateStr || dateStr.startsWith('0000')) return null;
  const nacimiento = new Date(`${dateStr}T00:00:00`);
  if (isNaN(nacimiento)) return null;
  const hoy = new Date();
  let edad = hoy.getFullYear() - nacimiento.getFullYear();
  const m = hoy.getMonth() - nacimiento.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) edad--;
  return edad >= 0 ? edad : null;
};

// ── Avatar reutilizable ───────────────────────────────────
const Avatar = ({ src, size = 38 }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: '50%',
      overflow: 'hidden',
      background: 'var(--sp-bg-alt, #eee)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {src ? (
      <img
        src={src}
        alt=""
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    ) : (
      <span style={{ fontSize: size * 0.4, color: 'var(--sp-primary)' }}>👤</span>
    )}
  </div>
);

// ── Modal propio de usuario ──────────────────────────────
const UsuarioModal = ({ usuario, onSave, onCancel, onFotoSubida }) => {
  const isEdit = !!usuario;
  const [form, setForm] = useState({
    email: usuario?.email || '',
    nombre: usuario?.nombre || '',
    apellido: usuario?.apellido || '',
    fecha_nacimiento: usuario?.fecha_nacimiento?.slice(0, 10) || '',
    password: '',
  });
  const [fotoUrl, setFotoUrl] = useState(usuario?.foto_perfil ? getImageUrl(usuario.foto_perfil) : null);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef(null);

  const edad = calcularEdad(form.fecha_nacimiento);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleFotoClick = () => fileInputRef.current?.click();

  const handleFotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !usuario?.id) return;

    setSubiendoFoto(true);
    setErrorMsg('');
    try {
      const res = await api.uploadPerfil(usuario.id, file);
      const nuevaRuta = res.data?.foto_perfil;
      setFotoUrl(nuevaRuta ? getImageUrl(nuevaRuta) + `?v=${Date.now()}` : fotoUrl);
      onFotoSubida?.(usuario.id, nuevaRuta);
    } catch (err) {
      setErrorMsg(err.response?.data?.error || 'No se pudo subir la foto');
    } finally {
      setSubiendoFoto(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setGuardando(true);
    try {
      await onSave(form);
    } catch (err) {
      setErrorMsg(err.message || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200, padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440,
          maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '1.1rem 1.25rem', borderBottom: '1px solid #eee',
        }}>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 800, margin: 0 }}>
            {isEdit ? 'Editar usuario' : 'Nuevo usuario'}
          </h2>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={22} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* Avatar */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
            <div style={{ position: 'relative' }}>
              <Avatar src={fotoUrl} size={88} />
              {isEdit && (
                <button
                  type="button"
                  onClick={handleFotoClick}
                  disabled={subiendoFoto}
                  title="Cambiar foto"
                  style={{
                    position: 'absolute', bottom: -2, right: -2, width: 32, height: 32,
                    borderRadius: '50%', background: 'var(--sp-primary)', color: '#fff',
                    border: '3px solid #fff', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {subiendoFoto ? <Loader2 size={14} className="sp-spin" /> : <Camera size={14} />}
                </button>
              )}
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={handleFotoChange} />
            </div>
            {!isEdit && (
              <span style={{ fontSize: '0.78rem', color: '#999', textAlign: 'center' }}>
                Podrás subir la foto luego de crear el usuario
              </span>
            )}
          </div>

          {/* Campos */}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.85rem', fontWeight: 700 }}>
            Email
            <input
              type="email" name="email" value={form.email} onChange={handleChange} required
              style={inputStyle}
            />
          </label>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.85rem', fontWeight: 700 }}>
              Nombre
              <input type="text" name="nombre" value={form.nombre} onChange={handleChange} required style={inputStyle} />
            </label>
            <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.85rem', fontWeight: 700 }}>
              Apellido
              <input type="text" name="apellido" value={form.apellido} onChange={handleChange} required style={inputStyle} />
            </label>
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.85rem', fontWeight: 700 }}>
            Fecha de nacimiento {edad !== null && <span style={{ fontWeight: 500, color: '#888' }}>· {edad} años</span>}
            <input type="date" name="fecha_nacimiento" value={form.fecha_nacimiento} onChange={handleChange} style={inputStyle} />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.85rem', fontWeight: 700 }}>
            {isEdit ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña'}
            <input
              type="password" name="password" value={form.password} onChange={handleChange}
              required={!isEdit} autoComplete="new-password" style={inputStyle}
            />
          </label>

          {errorMsg && (
            <div style={{ background: '#fee', color: '#c0392b', padding: '0.6rem 0.8rem', borderRadius: 8, fontSize: '0.85rem' }}>
              {errorMsg}
            </div>
          )}

          <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <button type="button" onClick={onCancel} className="sp-btn sp-btn--ghost">Cancelar</button>
            <button type="submit" disabled={guardando} className="sp-btn sp-btn--primary">
              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const inputStyle = {
  padding: '0.6rem 0.75rem',
  borderRadius: 8,
  border: '1px solid #ddd',
  fontSize: '0.92rem',
  outline: 'none',
};

// ── Panel principal ───────────────────────────────────────
const UsuariosPanel = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [search, setSearch] = useState('');

  const fetchUsuarios = async () => {
    try {
      setLoading(true);
      const res = await api.getUsuarios();
      setUsuarios(res.data?.data || res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsuarios(); }, []);

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return usuarios;
    return usuarios.filter(u =>
      u.email?.toLowerCase().includes(q) ||
      u.nombre?.toLowerCase().includes(q) ||
      u.apellido?.toLowerCase().includes(q)
    );
  }, [usuarios, search]);

  const handleEdit = (user) => { setSelected(user); setShowEdit(true); };
  const handleCreate = () => { setSelected(null); setShowEdit(true); };
  const handleDelete = (id) => { setSelected(usuarios.find(u => u.id === id)); setShowDelete(true); };

  // Actualiza la foto en memoria al instante, sin re-fetch completo
  const handleFotoSubida = (userId, nuevaRuta) => {
    setUsuarios((prev) => prev.map((u) => (u.id === userId ? { ...u, foto_perfil: nuevaRuta } : u)));
  };

  const handleSave = async (formData) => {
    const payload = { ...formData };
    if (!payload.password) delete payload.password;

    if (selected) {
      await api.updateUser(selected.id, payload);
    } else {
      if (!payload.password) throw new Error('La contraseña es obligatoria');
      await api.register(payload);
    }
    setShowEdit(false);
    fetchUsuarios();
  };

  const confirmDelete = async () => {
    try {
      await api.deleteUser(selected.id);
      setShowDelete(false);
      fetchUsuarios();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar');
    }
  };

  const columns = [
    {
      key: 'foto_perfil',
      label: '',
      render: (v) => <Avatar src={v ? getImageUrl(v) : null} />,
    },
    { key: 'nombre', label: 'Nombre' },
    { key: 'apellido', label: 'Apellido' },
    { key: 'email', label: 'Email' },
    {
      key: 'fecha_nacimiento',
      label: 'Fecha Nac.',
      render: (v) => formatFecha(v),
    },
    {
      id: 'edad',
      key: 'fecha_nacimiento',
      label: 'Edad',
      render: (v) => {
        const edad = calcularEdad(v);
        return edad !== null ? <Badge variant="neutral">{edad} años</Badge> : '—';
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800 }}>Usuarios</h1>
        <button className="sp-btn sp-btn--primary" onClick={handleCreate}>
          <Plus size={18} /> Nuevo
        </button>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <Badge variant="primary">Total: {usuarios.length}</Badge>
      </div>

      <div style={{ marginBottom: '1.25rem' }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Buscar por email, nombre o apellido..." />
      </div>

      <DataTable
        columns={columns}
        data={filtrados}
        loading={loading}
        error={error}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {showEdit && (
        <UsuarioModal
          usuario={selected}
          onSave={handleSave}
          onCancel={() => setShowEdit(false)}
          onFotoSubida={handleFotoSubida}
        />
      )}

      {showDelete && (
        <DeleteConfirm
          message={`¿Eliminar al usuario ${selected.email}?`}
          onConfirm={confirmDelete}
          onCancel={() => setShowDelete(false)}
        />
      )}
    </div>
  );
};

export default UsuariosPanel;